/**
 * Service for transforming search results into cost estimation data
 * and handling estimation calculations
 */

/**
 * Normalize an eBKP code to its canonical zero-padded spelling.
 * 'A1.1' -> 'A01.01', 'D8.6' -> 'D08.06'. The unpadded and padded spellings
 * are the SAME code (a data-entry difference, not different codes).
 *
 * @param {string} code - eBKP code in either spelling
 * @returns {string} - Normalized code
 */
export const normalizeEbkpCode = (code) => {
  if (!code) return code;
  const cleaned = String(code).trim().replace(/\s+/g, '');
  const match = cleaned.match(/^([A-Za-z])((?:\d+)(?:\.\d+)*)?$/);
  if (!match) return cleaned;
  const letter = match[1].toUpperCase();
  if (!match[2]) return letter;
  const segments = match[2].split('.').map((seg) => seg.padStart(2, '0'));
  return letter + segments.join('.');
};

/**
 * Normalize a unit string for matching, e.g. 'm² GSF ' -> 'm2 gsf'.
 * Used to match estimation row units ("m2 GSF") against SIA 416 workbook
 * metrics (unit "m2" + abbreviation "GSF").
 *
 * @param {string} text - Unit string
 * @returns {string} - Normalized key
 */
export const normalizeUnitKey = (text) =>
  String(text || '')
    .replace(/m²/g, 'm2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * Pick the ZIW index series for a row based on its mapped BKP codes:
 * Rohbau (BKP 20-22), Innenausbau (BKP 23-28), Übrige (BKP 29, 4, 5).
 * Falls back to Gesamtkosten when unmapped or ambiguous.
 *
 * @param {Array} bkpCodes - Mapped BKP codes of the row
 * @returns {string} - Series key (gesamt|rohbau|innenausbau|uebrige)
 */
export const seriesForBkpCodes = (bkpCodes) => {
  const series = new Set();
  (bkpCodes || []).forEach((code) => {
    const prefix2 = String(code).slice(0, 2);
    if (['20', '21', '22'].includes(prefix2)) {
      series.add('rohbau');
    } else if (['23', '24', '25', '26', '27', '28'].includes(prefix2)) {
      series.add('innenausbau');
    } else if (prefix2 === '29' || String(code)[0] === '4' || String(code)[0] === '5') {
      series.add('uebrige');
    }
  });
  return series.size === 1 ? [...series][0] : 'gesamt';
};

/**
 * Construction year of a project suggestion (from its Baudatum).
 *
 * @param {Object} suggestion - Project suggestion with projectInfo.year
 * @returns {number|null} - Year or null when not parsable
 */
export const suggestionYear = (suggestion) => {
  const parsed = parseInt(String(suggestion?.projectInfo?.year || '').slice(0, 4), 10);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Inflation factor for one row's selected project suggestion:
 * index(latest year) / index(project year) within the selected basis series.
 * The basis only defines WHICH index series to use — the increase always
 * runs from the project's year to the last known data point.
 *
 * Year resolution: the row's Jahr override (row.manualYear) wins; otherwise
 * the selected project's own Baudatum year. Years at or before the basis
 * date use the basis value (100). Without a resolvable year (or for years
 * newer than the index data) the price stays unadjusted.
 *
 * @param {Object} row - Table row carrying row.inflationContext / row.manualYear
 * @param {Object} suggestion - Project suggestion with projectInfo.year
 * @returns {number} - Multiplier for the suggestion's costPerUnit
 */
export const inflationFactorForSuggestion = (row, suggestion) => {
  const ctx = row && row.inflationContext;
  if (!ctx || !ctx.values) return 1;
  const latestVal = ctx.values[ctx.latestYear];
  if (!latestVal) return 1;

  const year = row.manualYear || suggestionYear(suggestion);
  if (!year || year >= ctx.latestYear) return 1;

  let projectVal = ctx.values[year];
  if (projectVal === undefined || projectVal === null) {
    if (year <= ctx.basisYear) {
      projectVal = 100; // the basis date is 100 by definition
    } else {
      return 1; // gap in the series
    }
  }
  return projectVal > 0 ? latestVal / projectVal : 1;
};

/**
 * Transform search results into estimation-ready data structure.
 * Seeds the table with the ENTIRE eBKP catalogue (normalized codes, with
 * mapped BKP codes), then attaches project suggestions from the search
 * results onto the normalized codes.
 *
 * @param {Object} searchResults - Results from search API
 * @param {Array} catalog - Full catalogue [{code, label, bkp}] (normalized codes)
 * @returns {Object} - Organized data by (normalized) eBKP code
 */
export const transformSearchResultsToEstimationData = (searchResults, catalog = []) => {
  const estimationData = {};

  // Seed every catalogue code so the whole eBKP catalogue appears
  catalog.forEach((entry) => {
    estimationData[entry.code] = {
      ebkpCode: entry.code,
      name: entry.label || '',
      bkpCodes: entry.bkp || [],
      unit: 'pausch.',
      level: calculateCodeLevel(entry.code),
      suggestions: []
    };
  });

  if (!searchResults || !searchResults.results) {
    return estimationData;
  }

  const catalogByCode = {};
  catalog.forEach((entry) => { catalogByCode[entry.code] = entry; });

  // Process each project in search results
  searchResults.results.forEach(project => {
    const { id, info, data } = project;

    if (!data || !Array.isArray(data)) {
      return;
    }

    // Process each row in project data
    // Data is an array of objects with keys: 'code', 'name', 'cost (CHF)', 'amount', 'cost/unit'
    data.forEach(row => {
      const rawCode = row['code'];
      const name = row['name'];
      const cost = row['cost (CHF)'];
      const amountStr = row['amount'];
      const costPerUnit = row['cost/unit'];

      // Skip if no code
      if (!rawCode) return;

      // Search data uses the unpadded spelling (A1.1) — populate the
      // normalized catalogue row (A01.01); same code, different data entry.
      const code = normalizeEbkpCode(rawCode);

      // Parse amount and unit from amount string (e.g., "100 m²")
      const { amount, unit } = parseAmountAndUnit(amountStr);

      // Initialize eBKP code entry if not in the catalogue
      if (!estimationData[code]) {
        estimationData[code] = {
          ebkpCode: code,
          name: name || '',
          bkpCodes: (catalogByCode[code] && catalogByCode[code].bkp) || [],
          unit: unit || 'pausch.',
          level: calculateCodeLevel(code),
          suggestions: []
        };
      }

      // Update name if current row has one and stored doesn't
      if (name && !estimationData[code].name) {
        estimationData[code].name = name;
      }

      // Prefer the concrete unit from the data over the catalogue default
      if (unit && estimationData[code].unit === 'pausch.') {
        estimationData[code].unit = unit;
      }

      // Add project suggestion if has valid data
      // Only add if amount and costPerUnit are valid numbers > 0
      const parsedCostPerUnit = parseFloat(costPerUnit);

      if (amount !== null && !isNaN(amount) && amount > 0 &&
          parsedCostPerUnit !== null && !isNaN(parsedCostPerUnit) && parsedCostPerUnit > 0) {
        estimationData[code].suggestions.push({
          projectId: id,
          projectName: info.Projektname || `Projekt ${id}`,
          projectInfo: {
            year: info.Baudatum || '',
            location: extractLocation(info.Adresse || ''),
            buildingType: info['Objektarten Hauptgruppe'] || '',
            gf: info.Geschossfläche || '',
            gv: info['Gebäudevolumen'] || '',
            hauptnutzflaeche: info['Hauptnutzfläche'] || '',
            oberirdisch: info['Geschossfläche oberirdisch'] || ''
          },
          amount: amount,
          cost: parseFloat(cost) || 0,
          costPerUnit: parsedCostPerUnit,
          unit: unit || 'pausch.'
        });
      }
    });
  });

  return estimationData;
};

/**
 * Parse amount string into numeric amount and unit
 * E.g., "100 m²" -> { amount: 100, unit: "m²" }
 * E.g., "13138 m2 GSF" -> { amount: 13138, unit: "m2 GSF" }
 *
 * @param {string} amountStr - Amount string from data
 * @returns {Object} - { amount: number, unit: string }
 */
const parseAmountAndUnit = (amountStr) => {
  if (!amountStr) {
    return { amount: null, unit: '' };
  }

  // Convert to string if not already
  const str = String(amountStr).trim();

  // Match pattern: number (with optional decimal/comma) followed by unit
  const match = str.match(/^([\d.,]+)\s*(.*)$/);

  if (match) {
    const numStr = match[1].replace(',', '.');
    const amount = parseFloat(numStr);
    const unit = match[2].trim();

    return {
      amount: isNaN(amount) ? null : amount,
      unit: unit || ''
    };
  }

  return { amount: null, unit: '' };
};

/**
 * Extract location from address string
 * E.g., "Musterstrasse 1, 8000 Zürich" -> "Zürich"
 *
 * @param {string} address - Full address
 * @returns {string} - Location name
 */
const extractLocation = (address) => {
  if (!address || typeof address !== 'string') {
    return '';
  }

  // Try to extract canton/city name (last word or after postal code)
  const parts = address.split(',');
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1].trim();
    const match = lastPart.match(/\d+\s+(.+)/);
    if (match) {
      return match[1].trim();
    }
    return lastPart;
  }

  return address;
};

/**
 * Calculate eBKP code hierarchy level
 * E.g., "A" -> 1, "A1" -> 2, "A1.1" -> 3, "A1.1.1" -> 4
 *
 * @param {string} code - eBKP code
 * @returns {number} - Hierarchy level
 */
export const calculateCodeLevel = (code) => {
  if (!code || typeof code !== 'string') {
    return 1;
  }

  const trimmed = code.trim();

  // Single letter = level 1 (A, B, C, etc.)
  if (/^[A-Z]$/.test(trimmed)) {
    return 1;
  }

  // Split by space to separate letter from numbers
  // "A 1" -> ["A", "1"]
  // "A1" -> ["A1"] (no space)
  const parts = trimmed.split(/\s+/);

  if (parts.length === 1) {
    // No space: could be "A1", "A1.1", etc.
    // Extract letter and numeric part
    const match = trimmed.match(/^([A-Z])(.*)$/);
    if (match) {
      const letter = match[1];
      const numeric = match[2];

      if (!numeric) {
        // Just letter: "A"
        return 1;
      }

      // Count dots in numeric part
      const dotCount = (numeric.match(/\./g) || []).length;
      // "1" -> level 2, "1.1" -> level 3, "1.1.1" -> level 4
      return 2 + dotCount;
    }
    return 1;
  }

  // Has space: "A 1", "A 1.1", etc.
  const numeric = parts.slice(1).join(' ');

  if (!numeric) {
    return 1;
  }

  // Count dots in numeric part
  const dotCount = (numeric.match(/\./g) || []).length;
  // "1" -> level 2, "1.1" -> level 3, "1.1.1" -> level 4
  return 2 + dotCount;
};

/**
 * Check if a code level is editable by users
 * Level 1 (A, B, C) is read-only, calculated from children
 * Level 2+ (A1, A1.1, etc.) is editable
 *
 * @param {number} level - Code level
 * @returns {boolean} - Whether level is editable
 */
export const isEditableLevel = (level) => {
  return level > 1;
};

/**
 * Build eBKP hierarchy tree structure
 * Organizes codes into parent-child relationships
 *
 * @param {Object} estimationData - Data organized by eBKP code
 * @returns {Array} - Hierarchical array of codes
 */
export const buildHierarchyTree = (estimationData) => {
  const codes = Object.keys(estimationData).sort();
  const tree = [];
  const codeMap = {};

  codes.forEach(code => {
    const item = {
      ...estimationData[code],
      children: []
    };
    codeMap[code] = item;

    // Find parent
    const parent = findParentCode(code, codes);
    if (parent && codeMap[parent]) {
      codeMap[parent].children.push(item);
    } else {
      tree.push(item);
    }
  });

  return tree;
};

/**
 * Find parent code in hierarchy
 * E.g., "A1.1" -> parent is "A1"
 *       "A1" -> parent is "A"
 *
 * @param {string} code - Child code
 * @param {Array} allCodes - All available codes
 * @returns {string|null} - Parent code or null
 */
const findParentCode = (code, allCodes) => {
  const trimmed = code.trim();

  // Single letter has no parent
  if (/^[A-Z]$/.test(trimmed)) {
    return null;
  }

  // Extract letter and numeric part
  const match = trimmed.match(/^([A-Z])\s*(.*)$/);
  if (!match) {
    return null;
  }

  const letter = match[1];
  const numeric = match[2];

  if (!numeric) {
    // Just letter, no parent
    return null;
  }

  // Check if numeric part has dots
  if (numeric.includes('.')) {
    // "A1.1.1" -> try "A1.1"
    // "A1.1" -> try "A1"
    const parts = numeric.split('.');
    const parentNumeric = parts.slice(0, -1).join('.');

    // Try with space
    let parentCode = `${letter} ${parentNumeric}`;
    if (allCodes.includes(parentCode)) {
      return parentCode;
    }

    // Try without space
    parentCode = `${letter}${parentNumeric}`;
    if (allCodes.includes(parentCode)) {
      return parentCode;
    }
  }

  // No dots, so parent is just the letter
  // "A1" -> parent is "A"
  // "A 1" -> parent is "A"
  if (allCodes.includes(letter)) {
    return letter;
  }

  return null;
};

/**
 * Flatten hierarchy tree for table display
 * Converts tree to flat array while preserving hierarchy info
 *
 * @param {Array} tree - Hierarchical tree
 * @returns {Object} - { flat: Array, codeMap: Object }
 */
export const flattenHierarchy = (tree) => {
  const flat = [];
  const codeMap = {};

  const traverse = (nodes, parentPath = [], parentCode = null) => {
    nodes.forEach(node => {
      const flatNode = {
        ...node,
        parentPath,
        parentCode,
        hasChildren: node.children && node.children.length > 0,
        childCodes: (node.children || []).map(child => child.ebkpCode)
      };

      flat.push(flatNode);
      codeMap[node.ebkpCode] = flatNode;

      if (node.children && node.children.length > 0) {
        traverse(node.children, [...parentPath, node.ebkpCode], node.ebkpCode);
      }
    });
  };

  traverse(tree);

  return { flat, codeMap };
};

/**
 * Get all descendants of a node (recursive)
 *
 * @param {string} code - Parent code
 * @param {Object} codeMap - Map of code to row data
 * @returns {Array} - Array of descendant codes
 */
export const getDescendants = (code, codeMap) => {
  const descendants = [];
  const node = codeMap[code];

  if (!node || !node.childCodes || node.childCodes.length === 0) {
    return descendants;
  }

  node.childCodes.forEach(childCode => {
    descendants.push(childCode);
    // Recursively get descendants of this child
    const childDescendants = getDescendants(childCode, codeMap);
    descendants.push(...childDescendants);
  });

  return descendants;
};

/**
 * Calculate total cost for a single row (leaf node)
 * Based on amount × costPerUnit
 *
 * @param {number} amount - Amount/quantity
 * @param {number} costPerUnit - Cost per unit
 * @returns {number} - Total cost
 */
export const calculateRowTotal = (amount, costPerUnit) => {
  if (!amount || !costPerUnit || isNaN(amount) || isNaN(costPerUnit)) {
    return 0;
  }
  return amount * costPerUnit;
};

/**
 * Format suggestion for dropdown display
 * Format: "123.45 CHF/m² · ProjectName · Year · Location · BuildingType · GF XXX m²"
 *
 * @param {Object} suggestion - Project suggestion
 * @returns {string} - Formatted label
 */
export const formatSuggestionLabel = (suggestion) => {
  const { projectName, projectInfo, costPerUnit, unit } = suggestion;

  // Format cost per unit at the beginning
  const costLabel = `${formatNumber(costPerUnit, 2)} CHF/${unit}`;

  const parts = [
    costLabel,
    projectName,
    projectInfo.year || '',
    projectInfo.location || '',
    projectInfo.buildingType || '',
    projectInfo.gf ? `GF ${formatNumber(projectInfo.gf)} m²` : ''
  ].filter(Boolean);

  return parts.join(' · ');
};

/**
 * Format number with thousands separator
 *
 * @param {number} num - Number to format
 * @param {number} decimals - Decimal places (default 0)
 * @returns {string} - Formatted number
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }

  return new Intl.NumberFormat('de-CH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

/**
 * Format currency
 *
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency
 */
export const formatCurrency = (amount) => {
  return `CHF ${formatNumber(amount, 2)}`;
};

/**
 * Parse CSV content for Menge (amount) values
 * Expected format: "eBKP,Menge" (one entry per line)
 * Handles headers, whitespace, and different separators
 *
 * @param {string} csvContent - Raw CSV file content
 * @returns {Object} - { success: [{code, amount}], skipped: [error messages] }
 */
export const parseCSVForMenge = (csvContent) => {
  if (!csvContent || typeof csvContent !== 'string') {
    return { success: [], skipped: ['CSV-Datei ist leer oder ungültig'] };
  }

  const success = [];
  const skipped = [];

  // Split by line breaks (handle different line endings)
  const lines = csvContent.split(/\r?\n/);

  // Detect separator (comma or semicolon)
  const separator = csvContent.includes(';') ? ';' : ',';

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Skip empty lines
    if (!trimmedLine) {
      return;
    }

    // Skip header line (case-insensitive detection)
    const lowerLine = trimmedLine.toLowerCase();
    if (lowerLine.includes('ebkp') || lowerLine.includes('menge') || lowerLine.includes('amount')) {
      return;
    }

    // Parse line
    const parts = trimmedLine.split(separator);

    if (parts.length < 2) {
      skipped.push(`Zeile ${index + 1}: Ungültiges Format (erwartet: eBKP${separator}Menge)`);
      return;
    }

    const code = parts[0].trim();
    const amountStr = parts[1].trim();

    // Validate code is not empty
    if (!code) {
      skipped.push(`Zeile ${index + 1}: eBKP-Code fehlt`);
      return;
    }

    // Parse amount (handle decimal comma and point)
    const normalizedAmount = amountStr.replace(',', '.');
    const amount = parseFloat(normalizedAmount);

    // Validate amount is a valid number
    if (isNaN(amount)) {
      skipped.push(`Zeile ${index + 1}: Ungültige Menge "${amountStr}" für Code "${code}"`);
      return;
    }

    // Validate amount is non-negative
    if (amount < 0) {
      skipped.push(`Zeile ${index + 1}: Negative Menge nicht erlaubt für Code "${code}"`);
      return;
    }

    // Success - add to results
    success.push({
      code: code,
      amount: amount
    });
  });

  return { success, skipped };
};

/**
 * Get all unique projects from all row suggestions
 * Used for master project selector dropdown
 *
 * @param {Array} tableRows - All table rows with suggestions
 * @returns {Array} - Array of unique projects: [{ projectId, projectName, projectInfo, count }]
 */
export const getAllUniqueProjects = (tableRows) => {
  if (!tableRows || !Array.isArray(tableRows)) {
    return [];
  }

  const projectMap = new Map();

  // Collect all projects from all rows
  tableRows.forEach(row => {
    if (!row.suggestions || !Array.isArray(row.suggestions)) {
      return;
    }

    row.suggestions.forEach(suggestion => {
      const projectId = suggestion.projectId;

      if (!projectMap.has(projectId)) {
        projectMap.set(projectId, {
          projectId: suggestion.projectId,
          projectName: suggestion.projectName,
          projectInfo: suggestion.projectInfo,
          count: 1
        });
      } else {
        // Increment count
        const existing = projectMap.get(projectId);
        existing.count++;
      }
    });
  });

  // Convert Map to Array and sort by project name
  const uniqueProjects = Array.from(projectMap.values());
  uniqueProjects.sort((a, b) => a.projectName.localeCompare(b.projectName));

  return uniqueProjects;
};

/**
 * Find if a project exists in a row's suggestions
 * Returns the suggestion index if found, -1 otherwise
 *
 * @param {Object} row - Table row with suggestions
 * @param {string|number} projectId - Project ID to find
 * @returns {number} - Index of suggestion or -1 if not found
 */
export const findProjectInRowSuggestions = (row, projectId) => {
  if (!row || !row.suggestions || !Array.isArray(row.suggestions)) {
    return -1;
  }

  return row.suggestions.findIndex(suggestion => suggestion.projectId === projectId);
};